// Sonuç paneli "Metraj" bölümü: özet tablo (1 m ve toplam), donatı toplamı, XLSX / CSV indirme.
import { t } from "../../i18n";
import { tEngine } from "../../i18n/engine";
import { useMemo, useState } from "react";
import { computeQuantities, fmt, type QuantitiesResult, type WallInput, type WallResult } from "@tasduvar/engine";
import { useProjectStore } from "../../state/projectStore";
import { useProvider } from "../../provider/useProvider";
import { Button } from "../../ui/Button";
import { downloadBlob, downloadText } from "../../lib/download";
import { buildQuantitiesCsv } from "../../export/csv";
import { completePricing } from "../project/PricingDialog";
import { projectFileName } from "../../storage";

/** Tek duvar metrajı XLSX / CSV indirme (sonuç paneli ve Metraj paftası ortak). */
export function useQuantitiesExport(wall: WallInput, q: QuantitiesResult): { csv: () => void; xlsx: () => Promise<void>; busy: boolean } {
  const [busy, setBusy] = useState(false);
  const project = useProjectStore((s) => s.project);
  const appendLog = useProvider((s) => s.appendLog);
  const base = project ? projectFileName(project).replace(/\.tasduvar\.json$/, "") : "tasduvar";
  const pricing = completePricing(project?.pricing);
  const log = (format: "xlsx" | "csv"): void => { if (project) void appendLog({ projectName: project.info.name, wallCount: 1, format, projectId: project.id }); };
  const csv = (): void => { downloadText(`${base}-metraj.csv`, buildQuantitiesCsv([{ name: wall.name, q }], pricing), "text/csv;charset=utf-8"); log("csv"); };
  const xlsx = async (): Promise<void> => {
    setBusy(true);
    try { const { buildQuantitiesXlsx } = await import("../../export/xlsx"); downloadBlob(`${base}-metraj.xlsx`, await buildQuantitiesXlsx([{ name: wall.name, q }], pricing, project?.info.name ?? "")); log("xlsx"); }
    finally { setBusy(false); }
  };
  return { csv, xlsx, busy };
}

export function QuantitiesSummary({ wall, result }: { wall: WallInput; result: WallResult }): JSX.Element {
  const [open, setOpen] = useState(false);
  const q = useMemo(() => computeQuantities(wall, result), [wall, result]);
  const { csv, xlsx, busy } = useQuantitiesExport(wall, q);
  const main = q.lines.filter((l) => !l.info);
  return (
    <div aria-label={t("Metraj özeti")}>
      <Button variant="ghost" className="!px-0" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{open ? "▾" : "▸"} {t("Metraj")} <span className="text-xs text-stone num">· L = {fmt(q.length, 1)} m{q.rebar ? ` · ${t("donatı")} ${fmt(q.rebar.total, 0)} kg` : ""}</span></Button>
      {open ? (<>
        <table className="w-full text-xs">
          <thead><tr className="border-b border-ink text-stone"><th className="text-left font-medium pb-1">{t("İmalat")}</th><th className="text-left font-medium">{t("Birim")}</th><th className="text-right font-medium">{t("1 m için")}</th><th className="text-right font-medium">{t("Toplam")}</th></tr></thead>
          <tbody>
            {main.map((l) => <tr key={l.key} className="border-t border-rule"><td className="py-0.5">{tEngine(l.label)}</td><td>{l.unit === "adet" ? t("adet") : l.unit}</td><td className="num text-right">{fmt(l.perMetre, l.unit === "kg" ? 1 : 2)}</td><td className="num text-right">{fmt(l.total, l.unit === "kg" ? 0 : 1)}</td></tr>)}
          </tbody>
        </table>
        <div className="flex items-center gap-2 mt-1">
          <Button variant="ghost" disabled={busy} onClick={() => void xlsx()}>XLSX</Button>
          <Button variant="ghost" onClick={csv}>CSV</Button>
          <span className="text-xs text-stone">{t("Ayrıntı ve keşif özeti raporda (13 / 13b).")}</span>
        </div>
      </>) : null}
    </div>
  );
}
